import { useQuery } from "@tanstack/react-query";
import type { PersonProjectAssignment } from "@/lib/persons/project-guidance";
import { personKeys } from "@/lib/query";

export interface PersonProjectsPayload {
  /**
   * False when the server has no gateway credentials. Distinct from an error —
   * the tab renders a "not connected" state rather than a failure, because an
   * environment without gateway access is expected, not broken.
   */
  connected: boolean;
  assignments: PersonProjectAssignment[];
}

/**
 * A person's project assignments and forward guidance, proxied from the Nymbl
 * API Gateway by `/api/persons/:id/projects`.
 *
 * This is a live read of NOS on every profile view, not a cached copy in our
 * database, so it is given a longer `staleTime` than a normal query — the data
 * changes on NOS's schedule, and re-fetching on every window focus would spend
 * a rate-limited gateway call for nothing.
 */
export function usePersonProjects(personId: string) {
  return useQuery({
    queryKey: personKeys.projects(personId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<PersonProjectsPayload> => {
      const res = await fetch(`/api/persons/${personId}/projects`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message =
          body !== null && typeof body === "object" && "error" in body
            ? String(body.error)
            : "Could not load projects";
        throw new Error(message);
      }
      const json = await res.json();
      return json.data;
    },
  });
}
